"use client";

import React from 'react';
import Image from 'next/image';
import { useLocale } from "@/context/LocaleContext";

export default function MultiEventFeature() {
  const { t } = useLocale();

  return (
    <div className="w-full">
      <section className="w-full bg-gray-50 py-16 md:py-24 mt-4">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row items-center gap-12 lg:gap-20">
            <div className="w-full md:w-[45%] flex justify-center">
              <div className="relative w-full max-w-[400px] aspect-[4/5] md:aspect-[3/4]">
                <Image 
                  src="/images/cta/split-tamu.webp" 
                  alt="Cara kerja Split Acara Setiap Tamu" 
                  fill 
                  className="object-contain drop-shadow-2xl" 
                />
              </div>
            </div>

            {/* Teks */}
            <div className="w-full md:w-[55%]">
              <h2 className="text-3xl md:text-4xl lg:text-[2.4rem] font-extrabold text-gray-900 mb-8 leading-tight tracking-tight">
                {t("MultiEvent.how_it_works") || "Bagaimana Cara Kerjanya?"}
              </h2>
              
              <div className="space-y-6 lg:space-y-8">
                {/* Langkah 1 */}
                <div className="flex items-start gap-4 lg:gap-5">
                  <span className="text-xl md:text-2xl font-black text-gray-900 mt-0.5">1.</span>
                  <div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-1.5">
                      {t("MultiEvent.step1_title") || "Buat Beberapa Acara dalam Satu Undangan"}
                    </h3>
                    <p className="text-gray-600 leading-relaxed text-[15px] md:text-[16px] lg:text-[17px]">
                      {t("MultiEvent.step1_desc") || "Kamu dapat menambahkan lebih dari satu acara dengan waktu, lokasi, dan detail yang berbeda."}
                    </p>
                  </div>
                </div>

                {/* Langkah 2 */}
                <div className="flex items-start gap-4 lg:gap-5">
                  <span className="text-xl md:text-2xl font-black text-gray-900 mt-0.5">2.</span>
                  <div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-1.5">
                      {t("MultiEvent.step2_title") || "Atur Akses Setiap Tamu"}
                    </h3>
                    <p className="text-gray-600 leading-relaxed text-[15px] md:text-[16px] lg:text-[17px]">
                      {t("MultiEvent.step2_desc") || "Tentukan tamu mana yang diundang ke acara tertentu dan tidak ke acara lainnya."}
                    </p>
                  </div>
                </div>

                {/* Langkah 3 */}
                <div className="flex items-start gap-4 lg:gap-5">
                  <span className="text-xl md:text-2xl font-black text-gray-900 mt-0.5">3.</span>
                  <div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-1.5">
                      {t("MultiEvent.step3_title") || "Undangan Ditampilkan Secara Personal"}
                    </h3>
                    <p className="text-gray-600 leading-relaxed text-[15px] md:text-[16px] lg:text-[17px]">
                      {t("MultiEvent.step3_desc") || "Tamu hanya melihat informasi acara yang memang relevan untuk mereka."}
                    </p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      <section className="w-full bg-white py-16 md:py-24 border-y border-gray-100">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="flex flex-col-reverse md:flex-row items-center gap-12 lg:gap-20">
            <div className="w-full md:w-[55%]">
              <h2 className="text-2xl md:text-3xl lg:text-[2.2rem] font-extrabold text-gray-900 mb-6 leading-tight tracking-tight">
                {t("MultiEvent.why_important_title") || "Kenapa Fitur Ini Penting?"}
              </h2>
              <p className="text-gray-700 text-[15px] md:text-[17px] leading-relaxed mb-4 font-medium">
                {t("MultiEvent.without_feature_desc") || "Tanpa pengaturan akses, sering terjadi:"}
              </p>
              <ul className="list-disc pl-5 space-y-2.5 text-gray-600 text-[15px] md:text-[17px] mb-5">
                <li>{t("MultiEvent.problem_1") || "Tamu hadir di acara yang bukan untuknya"}</li>
                <li>{t("MultiEvent.problem_2") || "Kebingungan jadwal dan lokasi"}</li>
                <li>{t("MultiEvent.problem_3") || "Kurangnya privasi untuk sesi tertentu"}</li>
              </ul>
              <p className="text-gray-700 text-[15px] md:text-[17px] leading-relaxed">
                {t("MultiEvent.solution_desc") || "Split Acara membantu semua itu tetap terkontrol."}
              </p>
            </div>

            <div className="w-full md:w-[45%] flex justify-center">
              <div className="relative w-full max-w-[300px] aspect-square">
                <Image 
                  src="/images/features/landing-page/interrogation.png" 
                  alt="Kenapa fitur ini penting" 
                  fill 
                  className="object-contain drop-shadow-lg" 
                />
              </div>
            </div>

          </div>
        </div>
      </section>

      <section className="w-full bg-gray-50 py-16 md:py-24 -mb-20">
        <div className="max-w-[1100px] mx-auto px-4 md:px-8">
          
          <h2 className="text-3xl md:text-4xl lg:text-[2.8rem] font-extrabold text-gray-900 mb-12 md:mb-16 text-center tracking-tight">
            {t("MultiEvent.main_benefits_title") || "Manfaat Utama"}
          </h2>

          {/* Grid Container */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            
            {/* CARD 1: Kontrol Akses Acara */}
            <div className="bg-white rounded-[24px] md:rounded-[27px] p-8 flex items-start gap-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-14 h-14 md:w-22 md:h-22 relative shrink-0">
                <Image src="/images/icons/key-access.svg" alt="Kontrol Akses" fill className="object-contain" />
              </div>
              <div>
                <h3 className="text-[1.1rem] md:text-[1.25rem] font-bold text-gray-900 mb-2">
                  {t("MultiEvent.card1_title") || "Kontrol Akses Acara"}
                </h3>
                <p className="text-gray-600 text-[14px] md:text-[15px] leading-relaxed">
                  {t("MultiEvent.card1_desc") || "Setiap tamu hanya dapat menghadiri acara yang memang diperuntukkan untuk mereka."}
                </p>
              </div>
            </div>

            {/* CARD 2: Lebih Rapi & Profesional */}
            <div className="bg-white rounded-[24px] md:rounded-[27px] p-8 flex items-center gap-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-14 h-14 md:w-22 md:h-22 relative shrink-0">
                <Image src="/images/icons/archive-event.svg" alt="Lebih Rapi" fill className="object-contain" />
              </div>
              <div>
                <h3 className="text-[1.1rem] md:text-[1.25rem] font-bold text-gray-900 mb-2">
                  {t("MultiEvent.card2_title") || "Lebih Rapi & Profesional"}
                </h3>
                <p className="text-gray-600 text-[14px] md:text-[15px] leading-relaxed">
                  {t("MultiEvent.card2_desc") || "Undangan terasa lebih tertata karena setiap tamu menerima informasi yang relevan."}
                </p>
              </div>
            </div>

            {/* CARD 3: Privasi Lebih Terjaga */}
            <div className="bg-white rounded-[24px] md:rounded-[27px] p-8 flex items-start gap-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-14 h-14 md:w-19 md:h-19 relative shrink-0">
                <Image src="/images/icons/private.svg" alt="Privasi Terjaga" fill className="object-contain" />
              </div>
              <div>
                <h3 className="text-[1.1rem] md:text-[1.25rem] font-bold text-gray-900 mb-2">
                  {t("MultiEvent.card3_title") || "Privasi Lebih Terjaga"}
                </h3>
                <p className="text-gray-600 text-[14px] md:text-[15px] leading-relaxed">
                  {t("MultiEvent.card3_desc") || "Sesi tertentu seperti akad atau acara terbatas tetap eksklusif."}
                </p>
              </div>
            </div>

            {/* CARD 4: Sangat Cocok untuk */}
            <div className="bg-white rounded-[24px] md:rounded-[27px] p-8 flex items-start gap-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-15 h-15 md:w-22 md:h-22 relative mt-10 shrink-0">
                <Image src="/images/icons/corect.svg" alt="Cocok Untuk" fill className="object-contain" />
              </div>
              <div>
                <h3 className="text-[1.1rem] md:text-[1.25rem] font-bold text-gray-900 mb-3">
                  {t("MultiEvent.card4_title") || "Sangat Cocok untuk"}
                </h3>
                <ul className="list-disc pl-5 space-y-1.5 text-gray-600 text-[14px] md:text-[15px] leading-relaxed">
                  <li>{t("MultiEvent.card4_li_1") || "Acara dengan segmentasi tamu"}</li>
                  <li>{t("MultiEvent.card4_li_2") || "Intimate wedding dengan kapasitas terbatas"}</li>
                  <li>{t("MultiEvent.card4_li_3") || "Event profesional yang memerlukan kontrol akses"}</li>
                </ul>
              </div>
            </div>

            {/* CARD 5: Terintegrasi dengan Fitur Lain (FULL WIDTH) */}
            <div className="md:col-span-2 bg-white rounded-[24px] md:rounded-[27px] p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-8 md:gap-12 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="flex-1 w-full order-2 md:order-1">
                <h3 className="text-[1.1rem] md:text-[1.3rem] font-bold text-gray-900 mb-3">
                  {t("MultiEvent.card5_title") || "Terintegrasi dengan Fitur Lain"}
                </h3>
                <p className="text-gray-600 text-[14px] md:text-[15px] leading-relaxed mb-3">
                  {t("MultiEvent.card5_desc") || "Split Acara Setiap Tamu terhubung langsung dengan:"}
                </p>
                <ul className="list-disc pl-5 space-y-1.5 text-gray-700 text-[14px] md:text-[15px] leading-relaxed mb-4">
                  <li className="font-semibold">{t("MultiEvent.card5_li_1") || "Custom RSVP"}</li>
                  <li className="font-semibold">{t("MultiEvent.card5_li_2") || "QR Code Check-in Buku Tamu"}</li>
                  <li className="font-semibold">{t("MultiEvent.card5_li_3") || "Reminder Acara Otomatis"}</li>
                </ul>
                <p className="text-gray-800 font-medium text-[14px] md:text-[15px] leading-relaxed">
                  {t("MultiEvent.card5_footer") || "Data kehadiran dan akses acara selalu sinkron."}
                </p>
              </div>
              <div className="w-40 h-40 md:w-60 md:h-60 md:mr-42 relative shrink-0 order-1 md:order-2">
                <Image src="/images/icons/setting-event.svg" alt="Terintegrasi" fill className="object-contain" />
              </div>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
}